import type * as yup from 'yup';
import type { InferType } from 'yup';
import { computed, ref, watch } from 'vue';
import type { LocationQuery, LocationQueryRaw } from 'vue-router';

import type { SortOrder } from '../sort';

type QueryFilterSchemaShape<Column extends string = string> = yup.AnyObject & {
  page?: number;
  column?: Column;
  order?: SortOrder;
};

type QueryFilterOrderBy<Schema extends yup.ObjectSchema<QueryFilterSchemaShape>> = {
  column: NonNullable<InferType<Schema>['column']>;
  order: NonNullable<InferType<Schema>['order']>;
};

export const useQueryFilter = <Column extends string, Schema extends yup.ObjectSchema<QueryFilterSchemaShape<Column>>>(
  schema: Schema,
  syncUrl?: boolean,
) => {
  const route = useRoute();
  const router = useRouter();

  const getValidateObject = (query: LocationQuery | LocationQueryRaw) => {
    try {
      const values = schema.validateSync(query, {
        stripUnknown: true,
        abortEarly: false,
        context: { isForURL: true },
      });
      return values;
    } catch {
      return {} as InferType<Schema>;
    }
  };

  const filters = ref<InferType<Schema>>(getValidateObject(route.query));

  watch(
    () => route.query,
    (query) => {
      filters.value = getValidateObject(query);
    },
    { immediate: true },
  );

  const updateFilters = (newState: LocationQueryRaw) => {
    filters.value = getValidateObject(newState);
    return filters.value;
  };

  const onUpdateQuery = (newState: LocationQueryRaw) => {
    const validValues = updateFilters(newState);
    if (syncUrl) {
      router.replace({ query: validValues });
    }
  };

  const setUrl = (newState: LocationQueryRaw) => {
    onUpdateQuery({
      ...newState,
      page: undefined,
      column: filters.value.column,
      order: filters.value.order,
    });
  };

  const onPage = (newPage?: number) => onUpdateQuery({
    ...filters.value,
    page: (newPage && newPage > 1)
      ? newPage
      : undefined,
  });

  const onSorting = (column: Column, order?: SortOrder) => {
    const hasValues = column && order;
    onUpdateQuery({
      ...filters.value,
      column: hasValues ? column : undefined,
      order: hasValues ? order : undefined,
    });
  };

  const orderBy = computed<QueryFilterOrderBy<Schema> | undefined>(() => (filters.value.column && filters.value.order ? {
    column: filters.value.column,
    order: filters.value.order,
  } : undefined));

  return {
    filters,
    setUrl,
    onPage,
    onSorting,
    orderBy,
  };
};
